/**
 * `country` — `CountryValue` (`specs/M2-tier2-column-types.md` §5.12, `[09§A.7 country]`).
 *
 * `value` is `{"countryCode":"IT","countryName":"Italy","changed_at":…}` — camelCase, and both
 * keys are required on write. The fragment nests them: `country { code name }` `[cvs§2 country]`.
 * `text` is the **name** (`"French Polynesia"`, `"United States"`), never the code.
 *
 * The code→name table is monday's, not ours: we store the pair the caller sent and never
 * re-derive a name from a code, so a country monday renamed keeps the name it was stored with.
 * The editor's list comes from `packages/shared/src/columns/country/list.ts`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { COUNTRY_NAMES } from './list.js';
/** The picker's vocabulary travels with the type folder (CLAUDE.md rule 6). */
export { COUNTRY_LIST, COUNTRY_NAMES } from './list.js';
export const countryValueSchema = z.looseObject({
    countryCode: z.string().length(2),
    countryName: z.string(),
    changed_at: z.string().nullable().optional(),
});
import { countryFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** `settings` is `{}` — *"no configurable settings"* `[09§A.7 country]`. */
export const countrySettingsSchema = z.looseObject({ hide_footer: z.boolean().optional() });
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string') {
        const code = raw.trim().toUpperCase();
        if (code === '')
            return null;
        const name = COUNTRY_NAMES[code];
        if (name === undefined)
            throw new ColumnValueError('country', `"${raw}" is not an ISO 3166-1 alpha-2 code`);
        return { countryCode: code, countryName: name };
    }
    if (typeof raw !== 'object') {
        throw new ColumnValueError('country', `expected an object, received ${typeof raw}`);
    }
    const record = raw;
    // The fragment shape `{ country: { code, name } }` round-trips too.
    const nested = record.country;
    const source = (nested !== null && typeof nested === 'object' ? nested : record);
    const code = source.countryCode ?? source.code;
    if (code === null || code === undefined || code === '')
        return null;
    if (typeof code !== 'string' || code.length !== 2) {
        throw new ColumnValueError('country', '"countryCode" must be an ISO 3166-1 alpha-2 code');
    }
    const upper = code.toUpperCase();
    const rawName = source.countryName ?? source.name;
    const name = typeof rawName === 'string' && rawName !== '' ? rawName : COUNTRY_NAMES[upper];
    if (name === undefined)
        throw new ColumnValueError('country', `unknown country code "${code}"`);
    const value = { countryCode: upper, countryName: name };
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
export const countryColumn = defineColumnType({
    type: 'country',
    title: 'Country',
    description: 'A country, picked from the ISO 3166 list.',
    apiFragmentName: 'CountryValue',
    legacyTypeName: 'country',
    idPrefix: 'country',
    valueSchema: countryValueSchema,
    settingsSchema: countrySettingsSchema,
    defaultSettings: {},
    settingsStrSchema: countrySettingsSchema,
    writable: true,
    /** *"No `change_simple_column_value`"* `[09§A.7 country]`. */
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: countryFilterConditions,
    filterOperators: operatorsOf(countryFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    toText(value) {
        return value?.countryName ?? '';
    },
    toSortKeys(value) {
        return { text: textShadow(value?.countryName ?? null), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.countryCode === b.countryCode;
    },
    toActivityValue(value) {
        return value === null ? null : { countryCode: value.countryCode, countryName: value.countryName };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { countryCode: value.countryCode, countryName: value.countryName };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        return value === null ? null : value.countryCode;
    },
    fromMondaySettings(raw) {
        return countrySettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return {
            country: { code: value.countryCode, name: value.countryName },
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet by the **code** so two spellings of one country land in one bucket. */
    bucketsOf(value) {
        return value === null ? [] : [{ key: value.countryCode, label: value.countryName }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map